import { useJSONFetcher } from "@outplayed/json-fetcher";
import Icon from "components/Common/Icon";
import { useQueryString } from "hooks/general-hooks";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { getEncounterId, useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  PATCH,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDataHeroName,
  getDifficultyPath,
  getFormattedEncounterType,
  getFormattedNames,
  getHeroNameFromSelectedTree,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  getIsHealer,
  getMainStatData,
  isClassHunter,
} from "../../../lib/wowHelpers";
import { validParams } from "../../../query_parameters/valid_parameters";
import { WoWContext } from "../../App";
import DataDetails from "../../Common/DataDetails";
import NoData from "../../Common/NoData";
import OctagonTalentIcon from "../../Common/OctagonTalentIcon";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import PvEGearGuide from "../../GearGuide/PvEGearGuide";
import Layout from "../Layout";
import BuildTabs from "./BuildTabs";
import StatPriorities from "./StatPriorities";
import WOWTalentTreeSection from "./TalentTreeSection";
import TierSetContainer from "./TierSetContainer";
import HeroTalentSelector from "./HeroTalentSelector";
import { useHeroQueryParams } from "hooks/wow/heroQueryParams";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";

// TWWTODO: Add hero to parsing

const WOWBuildsContainer = (props) => {
  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const [selectedBuild, setSelectedBuild] = useState(encounterType === "raid" ? "single_target" : "all_affixes");
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { difficulty, affix } = useQueryString();
  const difficultyPath = getDifficultyPath(difficulty, encounterType);
  const { selectedHero, setSelectedHero } = useHeroQueryParams(displayClassName, displaySpecName);
  const dataHeroName = getDataHeroName(getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName));
  const dataOtherHeroName = getDataHeroName(
    getHeroNameFromSelectedTree(selectedHero === "left" ? "right" : "left", displayClassName, displaySpecName),
  );
  const heroSpecName = getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName);

  const { data: builds, loading: buildsIsLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${difficultyPath}/${dataClassName}/${dataClassName}_builds.json`,
    { ssr: true },
  );

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`https://static.bigbrain.gg/wow/tree/v15/${classType}_${specs?.[i]?.value}.json`, {
      ssr: true,
    });
    allTrees[specs?.[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSelectedBuild(encounterType === "raid" ? "single_target" : "all_affixes");
  }, [encounterType]);

  if (buildsIsLoading) {
    return null;
  }

  const classSpec = `${dataClassName}-${dataSpecName}`;
  const classSpecHero = `${dataClassName}-${dataSpecName}-${dataHeroName}`;
  const otherClassSpecHero = `${dataClassName}-${dataSpecName}-${dataOtherHeroName}`;
  const hasData =
    builds &&
    (affix && affix !== "all"
      ? (builds.affixes?.[affix]?.[encounterID]?.[classSpecHero]?.stats?.total ?? 0) > 0 ||
        (builds.affixes?.[affix]?.[encounterID]?.[otherClassSpecHero]?.stats?.total ?? 0) > 0
      : (builds[encounterType]?.[encounterID]?.[classSpecHero]?.stats?.total ?? 0) > 0 ||
        (builds[encounterType]?.[encounterID]?.[otherClassSpecHero]?.stats?.total ?? 0) > 0);

  const buildsData =
    hasData &&
    (affix && affix !== "all"
      ? builds.affixes[affix][encounterID][classSpecHero]
      : builds[encounterType][encounterID][classSpecHero]);
  const otherHeroBuildsData =
    hasData &&
    (affix && affix !== "all"
      ? builds.affixes[affix][encounterID][otherClassSpecHero]
      : builds[encounterType][encounterID][otherClassSpecHero]);
  const itemsAndCombosData =
    hasData &&
    (affix && affix !== "all" ? builds.affixes[affix][encounterID][classSpec] : builds[encounterType][encounterID][classSpec]);

  // If we're using default filters for raid or mythic+, then we want to use the selected multi-build
  let selectedTalentsData = buildsData;
  if (encounterType === "raid" && encounterID === "all") {
    selectedTalentsData = builds ? builds[selectedBuild]?.all[classSpecHero] : [];
  } else if (encounterType === "mythic" && encounterID === "all" && (!affix || affix === "all")) {
    if (selectedBuild === "all_affixes") {
      selectedTalentsData = builds ? builds.mythic.all[classSpecHero] : [];
    } else if (selectedBuild === "tyrannical" || selectedBuild === "fortified") {
      selectedTalentsData = builds ? builds.affixes[selectedBuild].all[classSpecHero] : [];
    }
  }

  // Show multi-builds for default filters for raid & mythic+, but hide for healers
  let showBuildTabs = false;
  if (getIsHealer(specType)) {
    showBuildTabs = false;
  } else if (encounterType === "raid" && encounterID === "all") {
    showBuildTabs = true;
  } else if (encounterType === "mythic" && encounterID === "all" && (!affix || affix === "all")) {
    showBuildTabs = true;
  }

  const { mainStat, statLabel, statTooltip } = getMainStatData(
    encounterType,
    specType,
    selectedTalentsData?.talents?.max_key,
    selectedTalentsData?.talents?.hps,
    selectedTalentsData?.talents?.dps,
  );

  const topSectionData = {
    encounterID: encounterID,
    encounterType: encounterType,
    dataClassName: dataClassName,
    dataSpecName: dataSpecName,
    chosenHero: {
      pickrate: builds ? buildsData?.stats?.pickrate : 0,
      dps: builds ? buildsData?.stats?.dps : 0,
      hps: builds ? buildsData?.stats?.hps : 0,
      parses: builds ? buildsData?.stats?.count : 0,
      tierLetter: builds ? buildsData?.stats?.tier_letter : "",
    },
    otherHero: {
      pickrate: builds ? otherHeroBuildsData?.stats?.pickrate : 0,
      dps: builds ? otherHeroBuildsData?.stats?.dps : 0,
      hps: builds ? otherHeroBuildsData?.stats?.hps : 0,
      parses: builds ? otherHeroBuildsData?.stats?.count : 0,
      tierLetter: builds ? otherHeroBuildsData?.stats?.tier_letter : "",
    },
    specType: specType,
  };

  const statPriorities = builds ? buildsData?.stat_priorities?.stat_priorities?.split("-") : [];

  const talentTreeSectionData = {
    classType,
    encounterType,
    mainStat,
    popularity: builds ? parseFloat(selectedTalentsData?.talents?.perc).toFixed(1) + "%" : "0%",
    selectedTalents: selectedTalentsData?.talents?.talent.split("-") ?? [],
    classAndSpecTree,
    specType,
    statLabel,
    statTooltip,
    allTrees,
    showViewMore: true,
    selectedHero,
  };

  const talentTreeContext = {
    allTrees,
    selectedTalents: selectedTalentsData?.talents?.talent.split("-") ?? [],
    specType,
    selectedHero,
  };

  const pageDescription = `${displaySpecName} ${displayClassName} talents with U.GG's best data for ${getFormattedEncounterType(
    encounterType,
  )}. The best ${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(displayClassName)} ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]
  } hero talents, from BIS Gear to stat priorities, for ${EXPANSION} ${PATCH}.`;

  const metaDescription = `The Best ${displaySpecName} ${displayClassName} talents, from BIS gear to stat priority with the new ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]} and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} hero talents, in ${EXPANSION}.`;

  return (
    <Layout
      className="wow-builds-container"
      classType={classType}
      filterPage="builds"
      metaTitle={`${displaySpecName} ${displayClassName} Hero Talents for ${getFormattedEncounterType(
        encounterType,
      )}: BIS Stats, Talents and Gear`}
      pageDescription={pageDescription}
      metaDescription={metaDescription}
      pageTitle={`Talents, Hero Specs, and Gear for ${getFormattedEncounterType(encounterType)}`}
      specType={specType}
    >
      <HeroTalentSelector
        displayClassName={displayClassName}
        displaySpecName={displaySpecName}
        activeHero={selectedHero}
        onClick={setSelectedHero}
        specType={specType}
        data={topSectionData}
      />
      {buildsData?.stats?.count > 0 && builds?.timestamp && (
        <DataDetails
          encounterType={encounterType}
          numLogsTitle="Total Parses"
          timestamp={DateTime.fromISO(builds.timestamp).toRelative()}
          parses={buildsData.stats.count}
          maxMythicKey={builds ? buildsData.stats.max_mythic_keys : 0}
          minMythicKey={builds ? buildsData.stats.min_mythic_keys : 0}
          useDetailedTitle
        />
      )}
      {hasData && buildsData?.stats?.count > 0 ? (
        <WoWContext.Provider value={talentTreeContext}>
          <div className="wow-recommended-build">
            <StatPriorities statPriorities={statPriorities} dataClassName={dataClassName} dataSpecName={dataSpecName} />
            {showBuildTabs ? (
              <BuildTabs activeTab={selectedBuild} onClick={setSelectedBuild} encounterType={encounterType} />
            ) : null}
            {builds ? <WOWTalentTreeSection {...talentTreeSectionData} /> : null}
            {builds ? (
              <PvEGearGuide
                builds={itemsAndCombosData.items}
                combos={itemsAndCombosData.combos}
                classType={classType}
                specType={specType}
                encounterType={encounterType}
              />
            ) : null}
            <TierSetContainer
              className={displayClassName}
              specName={displaySpecName}
              heroSpecName={heroSpecName}
              encounterType={encounterType}
            />
            <PageSectionCrossLinks
              classType={classType}
              description={`Our friends at Icy Veins have you covered, with in-depth guides on rotations, talent trees, stat priorities, and gear for ${displaySpecName} ${displayClassName}.`}
              guideData={[
                {
                  description: `Explore a streamlined guide for the essentials of ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pve-${getIcyVeinsRole(specType)}-easy-mode`,
                  title: "Easy Mode Guide",
                },
                {
                  description: `Unlock the optimal builds and talents for ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pve-${getIcyVeinsRole(specType)}-spec-builds${isClassHunter(classType)}-talents`,
                  title: "Builds and Talents Guide",
                },
                {
                  description: `Discover the key stats to elevate your ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pve-${getIcyVeinsRole(specType)}-stat-priority`,
                  title: "Stat Priority Guide",
                },
              ]}
              specType={specType}
            />
          </div>
        </WoWContext.Provider>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export const getIcon = (classType, id, color, size, key, spriteData, spritesheet) => {
  if (classType == 3 || classType == "choice") {
    return (
      <div key={key ?? id} style={{ zIndex: "100" }} spell-id={`${id}`}>
        <OctagonTalentIcon
          iconSize={size}
          color={color}
          staticDataImg={`https://static.bigbrain.gg/wow/spell/assets/${id}.webp`}
          talentSelectedNumber={undefined}
        />
      </div>
    );
  }

  return (
    <div key={key ?? id} style={{ zIndex: "100" }} spell-id={`${id}`}>
      <Icon
        iconSize={size}
        classType={classType == 2 || classType == "round" ? "circle" : "square"}
        color={color}
        talentTreeGreyout={false}
        talentSelectedNumber={undefined}
        staticDataImg={spriteData && spritesheet ? spritesheet : `https://static.bigbrain.gg/wow/spell/assets/${id}.webp`}
        spriteData={spriteData}
      />
    </div>
  );
};

export default WOWBuildsContainer;
